import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Modal,
	PasswordInput,
	Stack,
	Text,
	TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	loginStaff,
	readStaffLoginError,
} from "@/features/auth/api/auth-staff";
import { loginWithStaffSession } from "@/features/auth/lib/auth-store";
import {
	type LoginFormValues,
	loginSchema,
} from "@/features/auth/lib/login-schema";

type StaffLoginModalProps = {
	opened: boolean;
	onClose: () => void;
	onSignedIn: () => void;
};

export function StaffLoginModal({
	opened,
	onClose,
	onSignedIn,
}: StaffLoginModalProps) {
	const { t } = useTranslation();

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={t("login.title")}
			centered
			withCloseButton
			closeOnClickOutside
			closeOnEscape
			closeButtonProps={{ "aria-label": t("login.close") }}
		>
			<Text size="sm" c="dimmed" mb="md">
				{t("login.subtitle")}
			</Text>
			<StaffLoginForm onDone={onSignedIn} onCancel={onClose} />
		</Modal>
	);
}

function StaffLoginForm({
	onDone,
	onCancel,
}: {
	onDone: () => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation();
	const {
		control,
		handleSubmit,
		formState: { isSubmitting },
	} = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: { email: "", password: "" },
	});

	const onSubmit = handleSubmit(async (values) => {
		try {
			const session = await loginStaff(
				values.email.trim().toLowerCase(),
				values.password,
			);
			loginWithStaffSession(session);
			notifications.show({
				color: "green",
				title: t("login.signedIn"),
				message: session.account.role,
			});
			onDone();
		} catch (error) {
			notifications.show({
				color: "red",
				title: t("login.staffFailed"),
				message: await readStaffLoginError(error),
			});
		}
	});

	return (
		<form onSubmit={onSubmit}>
			<Stack gap="sm">
				<Controller
					name="email"
					control={control}
					render={({ field, fieldState }) => (
						<TextInput
							{...field}
							label={t("login.email")}
							placeholder={t("login.emailPlaceholder")}
							error={fieldState.error?.message}
							autoComplete="username"
						/>
					)}
				/>
				<Controller
					name="password"
					control={control}
					render={({ field, fieldState }) => (
						<PasswordInput
							{...field}
							label={t("login.password")}
							placeholder={t("login.passwordPlaceholder")}
							error={fieldState.error?.message}
							autoComplete="current-password"
						/>
					)}
				/>
				<Button type="submit" loading={isSubmitting} fullWidth>
					{t("login.submit")}
				</Button>
				<Button type="button" variant="default" fullWidth onClick={onCancel}>
					{t("login.close")}
				</Button>
			</Stack>
		</form>
	);
}
